// File: src/bridge/omnicine/export/ExportBridge.tsx
//
// ExportBridge — OmniCine export event router (authority-side)
// ------------------------------------------------------------
// Listens:  "omnicine:export"
// Emits:    "omnicine:export:result"
//
// Supports (Phase Podcast Export v1):
// - kind:   "podcast.v1"
// - format: "json" | "wav"
//
// WAV resolution via injected window.__oc_export hooks (no hard coupling):
// Priority:
//  1) window.__oc_export.renderPodcastWav(exp, opts?)  (escape hatch, full custom render)
//  2) window.__oc_export.resolvePodcastAudio / resolvePodcastMedia (preferred modular hooks)
//  3) window.__oc_export.resolveAudio (legacy hook)
//
// IMPORTANT:
// - Result payload is shaped for PodcastExportPanel.tsx:
//   ok, kind, format, blob, filename, mime, message, error, requestId
// - We DO NOT emit object URLs (panel owns URL.createObjectURL + revoke).
//
// Ops integration (witness-safe summaries only):
// - emits synktribe:ops:metric (flow + latency + custom counters)
// - emits synktribe:ops:violation on schema/unsupported/errors (no payload)

import * as React from "react";
import type { OmniCineExportGlobal } from "@/bridge/omnicine/export/exportGlobal";

import type { PodcastExportV1 } from "@/services/omnicine/export/podcastExportV1";
import {
  buildPodcastExportV1,
  renderPodcastExportToWavBlob,
} from "@/services/omnicine/export/podcastExportV1";

import {
  SYNK_OPS_METRIC_EVENT,
  SYNK_OPS_VIOLATION_EVENT,
  type SynkOpsMetricDetailV1,
  type SynkOpsViolationDetailV1,
} from "@/contracts/ops/OpsEvents";

/** Optional but nice: canonical event constants */
export const OMNICINE_EXPORT_EVENT = "omnicine:export";
export const OMNICINE_EXPORT_RESULT_EVENT = "omnicine:export:result";

type ExportFormat = "json" | "wav";

type ExportRequest = {
  requestId?: string;
  kind: string; // "podcast.v1"
  format: ExportFormat | string;
  filename?: string;

  // Either provide an already-built export...
  exp?: PodcastExportV1;

  // ...or provide stateLike+opts to build it.
  stateLike?: any;
  buildOpts?: any;

  // Optional render overrides
  wavOpts?: Record<string, any>;
};

type ExportResultDetail = {
  ok: boolean;
  requestId?: string;
  kind: string;
  format: ExportFormat | string;

  // success payload
  blob?: Blob;
  filename?: string;
  mime?: string;
  message?: string;

  // failure payload
  error?: string;
  stack?: string;
};

/* ------------------------------------------------------------
 * Ops-safe emitters (NO payload, NO PII)
 * ------------------------------------------------------------ */

const OPS_CAP_ID = "omnicine.export.bridge" as const;

function safeDispatch(name: string, detail: unknown): void {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    // never throw from ops emitters
  }
}

function emitOpsMetric(d: Omit<SynkOpsMetricDetailV1, "v" | "at">): void {
  safeDispatch(SYNK_OPS_METRIC_EVENT, {
    v: 1,
    at: Date.now(),
    ...d,
    capabilityId: d.capabilityId ?? OPS_CAP_ID,
  } satisfies SynkOpsMetricDetailV1);
}

function emitOpsViolation(d: Omit<SynkOpsViolationDetailV1, "v" | "at">): void {
  safeDispatch(SYNK_OPS_VIOLATION_EVENT, {
    v: 1,
    at: Date.now(),
    ...d,
  } satisfies SynkOpsViolationDetailV1);
}

/* ------------------------------------------------------------
 * Small flow/latency aggregators (bounded, deterministic)
 * ------------------------------------------------------------ */

type FlowBucket = {
  windowSec: number;
  startAt: number;
  count: number;
  dropped: number;
};

function bumpFlow(bucket: FlowBucket, channel: string, droppedDelta = 0): void {
  const now = Date.now();
  const winMs = bucket.windowSec * 1000;

  // rollover bucket deterministically
  if (now - bucket.startAt >= winMs) {
    bucket.startAt = now;
    bucket.count = 0;
    bucket.dropped = 0;
  }

  bucket.count += 1;
  bucket.dropped += droppedDelta;

  emitOpsMetric({
    kind: "flow",
    name: "event_flow",
    channel,
    windowSec: bucket.windowSec,
    value: bucket.count,
    dropped: bucket.dropped,
    band: bucket.dropped > 10 ? "critical" : bucket.dropped > 0 ? "warning" : "normal",
  });
}

const exportFlow10: FlowBucket = { windowSec: 10, startAt: Date.now(), count: 0, dropped: 0 };
const resultFlow10: FlowBucket = { windowSec: 10, startAt: Date.now(), count: 0, dropped: 0 };

// requestId -> startAt (bounded by map size)
const inflight = new Map<string, number>();

// ring buffer latencies for p95 estimate
const latRing: number[] = [];
const LAT_MAX = 50;

function pushLatency(ms: number): void {
  if (!Number.isFinite(ms) || ms < 0) return;
  latRing.push(ms);
  if (latRing.length > LAT_MAX) latRing.shift();

  // emit instantaneous latency (custom) every result
  emitOpsMetric({
    kind: "custom",
    name: "omnicine.export.latency_ms",
    value: ms,
    band: ms > 8000 ? "critical" : ms > 2500 ? "warning" : "normal",
  });

  // emit p95 estimate every 10 samples (cheap + stable)
  if (latRing.length >= 10 && latRing.length % 10 === 0) {
    const sorted = [...latRing].sort((a, b) => a - b);
    const idx = Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95));
    const p95 = sorted[idx];

    emitOpsMetric({
      kind: "latency",
      name: "omnicine.export.p95",
      p95Ms: p95,
      band: p95 > 8000 ? "critical" : p95 > 2500 ? "warning" : "normal",
    });
  }
}

/* ------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------ */

function safeJsonStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function safeFileStem(name: string): string {
  const s = String(name || "export").trim() || "export";
  return s.replace(/[^\w\-]+/g, "_").slice(0, 80) || "export";
}

function makeRequestId(): string {
  try {
    const c = globalThis.crypto as Crypto | undefined;
    if (c?.randomUUID) return c.randomUUID();
  } catch {
    // ignore
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function emitResult(detail: ExportResultDetail) {
  try {
    window.dispatchEvent(
      new CustomEvent(OMNICINE_EXPORT_RESULT_EVENT, { detail }),
    );
  } catch {
    // ignore
  }
}

function buildExport(req: ExportRequest): PodcastExportV1 {
  return (
    req.exp ??
    buildPodcastExportV1(
      req.stateLike ?? (window as any).__oc_state ?? null,
      req.buildOpts ?? {},
    )
  );
}

async function handlePodcastJson(req: ExportRequest): Promise<ExportResultDetail> {
  const exp = buildExport(req);

  const text = safeJsonStringify(exp);
  const mime = "application/json";
  const blob = new Blob([text], { type: mime });

  const stem = safeFileStem(
    (exp as any)?.title || (exp as any)?.name || "podcast",
  );
  const filename = req.filename || `${stem}.json`;

  return {
    ok: true,
    requestId: req.requestId,
    kind: "podcast.v1",
    format: "json",
    blob,
    filename,
    mime,
    message: "JSON export ready.",
  };
}

async function handlePodcastWav(req: ExportRequest): Promise<ExportResultDetail> {
  const exp = buildExport(req);

  const root: OmniCineExportGlobal | undefined = window.__oc_export;

  // (1) Escape hatch: fully custom render
  if (root?.renderPodcastWav) {
    const out = await root.renderPodcastWav(exp, req.wavOpts);

    const blob = out instanceof Blob ? out : out.blob;
    const suggestedName = out instanceof Blob ? undefined : out.filename;

    const stem = safeFileStem(
      (exp as any)?.title || (exp as any)?.name || "podcast",
    );
    const filename = req.filename || suggestedName || `${stem}.wav`;

    return {
      ok: true,
      requestId: req.requestId,
      kind: "podcast.v1",
      format: "wav",
      blob,
      filename,
      mime: blob.type || "audio/wav",
      message: "WAV render ready.",
    };
  }

  // (2) Preferred modular hooks
  const resolvePodcastAudio = root?.resolvePodcastAudio;
  const resolvePodcastMedia = root?.resolvePodcastMedia;

  // (3) Legacy fallback
  const resolveAudioLegacy = root?.resolveAudio;

  const resolveAudio = async (args: any): Promise<AudioBuffer> => {
    if (resolvePodcastAudio) return resolvePodcastAudio(args);
    if (resolveAudioLegacy) return resolveAudioLegacy(args);

    // Ops violation (safe): missing resolver
    emitOpsViolation({
      severity: "warning",
      channel: OMNICINE_EXPORT_EVENT,
      where: "ExportBridge",
      reason: "missing_audio_resolver",
      schema: "podcast.v1/wav",
    });

    throw new Error(
      `WAV export requires an audio resolver. Install via installExportAudioResolver() ` +
        `or provide window.__oc_export.renderPodcastWav(exp, opts).`,
    );
  };

  const resolveMedia =
    resolvePodcastMedia && typeof resolvePodcastMedia === "function"
      ? async (args: any) => {
          try {
            return await resolvePodcastMedia(args);
          } catch {
            return null;
          }
        }
      : undefined;

  const blob = await renderPodcastExportToWavBlob(exp, {
    ...(req.wavOpts || {}),
    resolveAudio,
    ...(resolveMedia ? { resolveMedia } : {}),
  });

  const stem = safeFileStem(
    (exp as any)?.title || (exp as any)?.name || "podcast",
  );
  const filename = req.filename || `${stem}.wav`;

  return {
    ok: true,
    requestId: req.requestId,
    kind: "podcast.v1",
    format: "wav",
    blob,
    filename,
    mime: blob.type || "audio/wav",
    message: "WAV render ready.",
  };
}

/**
 * Named export (top-level):
 * Listens for export results and triggers browser download.
 * Returns an uninstall function.
 *
 * HMR-safe: replaces any prior installed downloader listener.
 */
export function installExportResultDownloader(opts?: {
  dryRun?: boolean;
  shouldDownload?: (r: any) => boolean;
}): () => void {
  const dryRun = !!opts?.dryRun;
  const shouldDownload =
    typeof opts?.shouldDownload === "function"
      ? opts.shouldDownload
      : (r: any) => !!r?.ok && r?.blob instanceof Blob;

  // HMR-safe singleton listener slot
  window.__oc_export = window.__oc_export || {};
  const root = window.__oc_export;

  if (root.__downloaderListener) {
    try {
      window.removeEventListener(
        OMNICINE_EXPORT_RESULT_EVENT,
        root.__downloaderListener,
      );
    } catch {}
    try {
      delete root.__downloaderListener;
    } catch {}
  }

  const onResult = (e: Event) => {
    const detail = (e as CustomEvent).detail as any;
    if (!detail) return;
    if (!shouldDownload(detail)) return;

    const blob: Blob | undefined =
      detail.blob instanceof Blob ? detail.blob : undefined;
    if (!blob) return;

    const filename =
      typeof detail.filename === "string" && detail.filename.trim()
        ? detail.filename.trim()
        : "export.bin";

    // Ops-safe: count downloads, never expose names/payload
    emitOpsMetric({
      kind: "custom",
      name: "omnicine.export.download_triggered",
      value: 1,
      band: "normal",
      capabilityId: "omnicine.export.downloader",
    });

    if (dryRun) {
      // eslint-disable-next-line no-console
      console.log("[ExportBridge] download(dryRun)", {
        filename,
        size: blob.size,
        type: blob.type,
      });
      return;
    }

    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noreferrer";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      // allow click to propagate then revoke
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  };

  root.__downloaderListener = onResult as EventListener;
  root.__downloaderInstalledAt =
    typeof performance !== "undefined" ? performance.now() : Date.now();

  window.addEventListener(
    OMNICINE_EXPORT_RESULT_EVENT,
    root.__downloaderListener,
  );

  return () => {
    try {
      window.removeEventListener(
        OMNICINE_EXPORT_RESULT_EVENT,
        onResult as EventListener,
      );
      if (root.__downloaderListener === (onResult as any)) {
        delete root.__downloaderListener;
      }
    } catch {}
  };
}

export default function ExportBridge(): JSX.Element | null {
  React.useEffect(() => {
    // HMR-safe singleton listener
    window.__oc_export = window.__oc_export || {};
    const root = window.__oc_export;

    if (root.__bridgeListener) {
      try {
        window.removeEventListener(OMNICINE_EXPORT_EVENT, root.__bridgeListener);
      } catch {}
      try {
        delete root.__bridgeListener;
      } catch {}
    }

    const onExport = async (e: Event) => {
      const detail = (e as CustomEvent).detail as ExportRequest | undefined;
      const req: ExportRequest = detail || ({} as any);

      const kind = String(req.kind || "").trim();
      const formatRaw = String(req.format || "").trim().toLowerCase();
      const format: ExportFormat | string = formatRaw;

      const requestId =
        (req.requestId && String(req.requestId)) || makeRequestId();

      // flow: export request observed
      bumpFlow(exportFlow10, OMNICINE_EXPORT_EVENT);

      // record latency start (bounded map)
      if (inflight.size > 200) {
        // simple bound: clear if runaway
        inflight.clear();
      }
      inflight.set(requestId, Date.now());

      try {
        if (kind !== "podcast.v1") {
          emitOpsViolation({
            severity: "warning",
            channel: OMNICINE_EXPORT_EVENT,
            where: "ExportBridge",
            reason: "unsupported_kind",
            schema: String(kind || "unknown"),
          });
          throw new Error(`Unsupported export kind: "${kind}"`);
        }
        if (format !== "json" && format !== "wav") {
          emitOpsViolation({
            severity: "warning",
            channel: OMNICINE_EXPORT_EVENT,
            where: "ExportBridge",
            reason: "unsupported_format",
            schema: String(format || "unknown"),
          });
          throw new Error(
            `Unsupported export format: "${format}" (expected "json" or "wav")`,
          );
        }

        const out =
          format === "json"
            ? await handlePodcastJson({ ...req, requestId, kind, format })
            : await handlePodcastWav({ ...req, requestId, kind, format });

        emitResult(out);

        // flow: result emitted
        bumpFlow(resultFlow10, OMNICINE_EXPORT_RESULT_EVENT);

        // latency capture
        const startedAt = inflight.get(requestId);
        inflight.delete(requestId);
        if (typeof startedAt === "number") {
          pushLatency(Date.now() - startedAt);
        }

        // success counter
        emitOpsMetric({
          kind: "custom",
          name: "omnicine.export.success",
          value: 1,
          band: "normal",
        });
      } catch (err: any) {
        emitResult({
          ok: false,
          requestId,
          kind: kind || req.kind || "unknown",
          format: format || req.format || "unknown",
          error: String(err?.message || err),
          ...(err?.stack ? { stack: String(err.stack) } : {}),
        });

        bumpFlow(resultFlow10, OMNICINE_EXPORT_RESULT_EVENT);

        const startedAt = inflight.get(requestId);
        inflight.delete(requestId);
        if (typeof startedAt === "number") {
          pushLatency(Date.now() - startedAt);
        }

        emitOpsMetric({
          kind: "custom",
          name: "omnicine.export.failure",
          value: 1,
          band: "warning",
        });

        emitOpsViolation({
          severity: "warning",
          channel: OMNICINE_EXPORT_RESULT_EVENT,
          where: "ExportBridge",
          reason: "export_failed",
          schema: "podcast.v1",
        });
      }
    };

    root.__bridgeListener = onExport as EventListener;
    root.__bridgeInstalledAt =
      typeof performance !== "undefined" ? performance.now() : Date.now();

    window.addEventListener(OMNICINE_EXPORT_EVENT, root.__bridgeListener);

    // One-time “bridge alive” marker metric
    emitOpsMetric({
      kind: "custom",
      name: "omnicine.export.bridge_installed",
      value: 1,
      band: "normal",
    });

    return () => {
      try {
        if (root.__bridgeListener) {
          window.removeEventListener(OMNICINE_EXPORT_EVENT, root.__bridgeListener);
          delete root.__bridgeListener;
        }
      } catch {}
    };
  }, []);

  return null;
}
